import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SOCKET_EVENTS } from '@maios/shared';
import { getSocket } from '../lib/socket';
import { useAuthStore } from '../store/authStore';
import {
  getMessages,
  sendMessage as sendMessageApi,
  markAsRead as markAsReadApi,
  type ChatMessageItem,
} from '../services/chatChannelService';

const MESSAGE_LIMIT = 50;

type NewMessageCallback = (msg: ChatMessageItem, isMine: boolean) => void;

interface ChannelChatContextValue {
  messages: ChatMessageItem[];
  isLoading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  sending: boolean;
  sendMessage: (content: string, attachments?: unknown[], mentions?: unknown[]) => Promise<void>;
  loadMore: () => void;
  markAsRead: () => void;
  onNewMessage: (cb: NewMessageCallback) => () => void;
}

const ChannelChatContext = createContext<ChannelChatContextValue | null>(null);

export function useChannelChat() {
  const ctx = useContext(ChannelChatContext);
  if (!ctx) throw new Error('useChannelChat must be used within ChannelChatProvider');
  return ctx;
}

export function ChannelChatProvider({ channelId, children }: { channelId: string; children: ReactNode }) {
  const userId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);

  const [listeners] = useState<Set<NewMessageCallback>>(() => new Set());

  const onNewMessage = useCallback((cb: NewMessageCallback) => {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, [listeners]);

  const invalidateSidebar = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
  }, [queryClient]);

  // Load messages + join socket room
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setMessages([]);
    setHasMore(true);
    setNextCursor(null);

    getMessages(channelId, { limit: MESSAGE_LIMIT })
      .then((data) => {
        if (cancelled) return;
        setMessages(data.messages);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
        setIsLoading(false);
      })
      .catch((err) => { console.warn('[ChannelChat] fetchMessages failed:', err); if (!cancelled) setIsLoading(false); });

    // Mark as read + invalidate sidebar
    markAsReadApi(channelId).then(() => invalidateSidebar()).catch((err) => console.warn('[ChannelChat] markAsRead failed:', err));

    const socket = getSocket();
    if (!socket) {
      return () => { cancelled = true; };
    }

    // Join channel room + also project chat room for compatibility
    socket.emit(SOCKET_EVENTS.CHANNEL_JOIN, { channelId });

    const handleNewMessage = (msg: ChatMessageItem) => {
      // Accept messages for this channel (from both CHANNEL_NEW_MESSAGE)
      if (msg.channelId !== channelId) return;
      setMessages((prev) => {
        if (prev.some((m) => m.messageId === msg.messageId)) return prev;
        return [...prev, msg];
      });
      const isMine = msg.sender.userId === userId;
      listeners.forEach((cb) => cb(msg, isMine));
      // Mark as read + invalidate sidebar
      markAsReadApi(channelId).then(() => invalidateSidebar()).catch((err) => console.warn('[ChannelChat] markAsRead failed:', err));
    };

    // Listen for channel messages (from channel:newMessage)
    socket.on(SOCKET_EVENTS.CHANNEL_NEW_MESSAGE, handleNewMessage);
    // Also listen for legacy chat messages (from chat:newMessage for project compatibility)
    socket.on(SOCKET_EVENTS.CHAT_NEW_MESSAGE, (msg: Record<string, unknown>) => {
      if (msg.channelId === channelId) {
        handleNewMessage(msg as unknown as ChatMessageItem);
      }
    });

    // CHG-075-P3: message edit/delete events
    const handleEdited = (data: { messageId: string; channelId: string; content: string; isEdited: boolean; editedAt: string }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId
          ? { ...m, content: data.content, isEdited: data.isEdited, editedAt: data.editedAt } as ChatMessageItem
          : m,
      ));
    };
    const handleDeleted = (data: { messageId: string; channelId: string }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId
          ? { ...m, content: '삭제된 메시지입니다', isDeleted: true, mentions: [], resolvedMentions: [] } as ChatMessageItem
          : m,
      ));
    };
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_EDITED, handleEdited);
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_DELETED, handleDeleted);

    // Reaction updates
    const handleReaction = (data: { messageId: string; channelId: string; reactions: unknown[] }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId ? { ...m, reactions: data.reactions } as ChatMessageItem : m,
      ));
    };
    socket.on(SOCKET_EVENTS.CHANNEL_REACTION_UPDATED, handleReaction);

    // CHG-075-P4: 스레드 메시지 수신 → 부모 메시지 threadCount/lastThreadAt 업데이트
    const handleThreadMessage = (data: { parentMessageId: string; message: ChatMessageItem; threadCount: number }) => {
      if (data.message.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) => {
        if (m.messageId !== data.parentMessageId) return m;
        const prevPreview = m.threadPreview ?? [];
        const newSender = data.message.sender;
        const updatedPreview = prevPreview.some((p) => p.userId === newSender.userId)
          ? prevPreview
          : [newSender, ...prevPreview].slice(0, 3);
        return {
          ...m,
          threadCount: data.threadCount,
          lastThreadAt: data.message.createdAt,
          threadPreview: updatedPreview,
        };
      }));
    };
    socket.on(SOCKET_EVENTS.CHANNEL_THREAD_MESSAGE, handleThreadMessage);

    // CHG-075-P4: 고정/해제 이벤트 → 메시지 isPinned 업데이트
    const handlePinned = (data: { channelId: string; messageId: string }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId ? { ...m, isPinned: true } : m,
      ));
    };
    const handleUnpinned = (data: { channelId: string; messageId: string }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId ? { ...m, isPinned: false } : m,
      ));
    };
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_PINNED, handlePinned);
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_UNPINNED, handleUnpinned);

    // CHG-123 REQ-V123-014: linkedResources 실시간 갱신
    const handleLinkedResourceUpdated = (data: { messageId: string; channelId: string; linkedResources: ChatMessageItem['linkedResources'] }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId ? { ...m, linkedResources: data.linkedResources } as ChatMessageItem : m,
      ));
    };
    socket.on(SOCKET_EVENTS.CHAT_MESSAGE_LINKED_RESOURCE_UPDATED, handleLinkedResourceUpdated);

    return () => {
      cancelled = true;
      socket.emit(SOCKET_EVENTS.CHANNEL_LEAVE, { channelId });
      socket.off(SOCKET_EVENTS.CHANNEL_NEW_MESSAGE, handleNewMessage);
      socket.off(SOCKET_EVENTS.CHAT_NEW_MESSAGE);
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_EDITED, handleEdited);
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_DELETED, handleDeleted);
      socket.off(SOCKET_EVENTS.CHANNEL_REACTION_UPDATED, handleReaction);
      socket.off(SOCKET_EVENTS.CHANNEL_THREAD_MESSAGE, handleThreadMessage);
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_PINNED, handlePinned);
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_UNPINNED, handleUnpinned);
      socket.off(SOCKET_EVENTS.CHAT_MESSAGE_LINKED_RESOURCE_UPDATED, handleLinkedResourceUpdated);
    };
  }, [channelId, userId, listeners, invalidateSidebar]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    getMessages(channelId, { cursor: nextCursor ?? undefined, limit: MESSAGE_LIMIT })
      .then((data) => {
        setMessages((prev) => [...data.messages, ...prev]);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
      })
      .catch((err) => console.warn('[ChannelChat] loadMore failed:', err))
      .finally(() => setLoadingMore(false));
  }, [channelId, nextCursor, hasMore, loadingMore]);

  const sendMessage = useCallback(async (content: string, attachments?: unknown[], mentions?: unknown[]) => {
    setSending(true);
    try {
      const msg = await sendMessageApi(
        channelId, content,
        attachments as import('../services/chatChannelService').ChatAttachment[] | undefined,
        undefined,
        mentions as import('../services/chatChannelService').MentionData[] | undefined,
      );
      setMessages((prev) => {
        if (prev.some((m) => m.messageId === msg.messageId)) return prev;
        return [...prev, msg];
      });
      listeners.forEach((cb) => cb(msg, true));
      invalidateSidebar();
    } finally {
      setSending(false);
    }
  }, [channelId, listeners, invalidateSidebar]);

  const markAsRead = useCallback(() => {
    markAsReadApi(channelId).then(() => invalidateSidebar()).catch((err) => console.warn('[ChannelChat] markAsRead failed:', err));
  }, [channelId, invalidateSidebar]);

  const value = useMemo<ChannelChatContextValue>(() => ({
    messages, isLoading, loadingMore, hasMore, sending,
    sendMessage, loadMore, markAsRead, onNewMessage,
  }), [messages, isLoading, loadingMore, hasMore, sending, sendMessage, loadMore, markAsRead, onNewMessage]);

  return <ChannelChatContext.Provider value={value}>{children}</ChannelChatContext.Provider>;
}
